import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../lib/api";

/**
 * Every agent key on the account, across spaces (ADR 000030).
 *
 * Read and revoke only. One place to see what exists and stop it is what
 * somebody wants when a laptop goes missing, and it has to span spaces to
 * answer that question. Creating one is not offered here, because a key belongs
 * to a single space and there is no space in this context to create it for
 * (ADR 000041).
 */
export function AgentKeysRoute() {
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);

  const keys = useQuery({ queryKey: ["agent-keys"], queryFn: () => api.listAgentKeys() });
  const spaces = useQuery({ queryKey: ["spaces"], queryFn: () => api.listSpaces() });

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeAgentKey(id as never),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["agent-keys"] });
    },
    onError: (error) => setFailure(describe(error)),
  });

  // A key names a space it can reach, and its holder is a member of that space,
  // so this lookup is complete. Falling back to the id keeps a key visible and
  // revokable even if the space listing has not arrived.
  const spaceName = (id: string) => spaces.data?.find((space) => space.id === id)?.name ?? id;

  return (
    <section>
      <header className="page-head">
        <h1>Agent keys</h1>
        <p className="muted">
          For the MCP server, and anything else that reads scenix without a person sitting in front
          of it. Each key works in one space and can reach nothing else, so they are made from
          inside the space they are for. This is every key you hold, wherever it was made.
        </p>
      </header>

      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      {keys.isPending ? (
        <p role="status">Loading…</p>
      ) : keys.error ? (
        <p className="error">{describe(keys.error)}</p>
      ) : keys.data.length === 0 ? (
        <p className="muted">
          No keys yet. Open a space and choose Agent keys to make one, or{" "}
          <Link to="/spaces">pick a space</Link>.
        </p>
      ) : (
        <ul className="keys">
          {keys.data.map((key) => (
            <li key={key.id} className={key.revokedAt ? "is-revoked" : ""}>
              <span className="key-body">
                <strong>{key.name}</strong>
                <span className="key-meta">
                  <Link to={`/spaces/${key.spaceId}/keys`}>{spaceName(key.spaceId)}</Link>
                  {" · "}
                  {/* Enough to tell two keys apart, and not enough to use. */}
                  sk_scenix_…{key.hint}
                  {" · "}
                  created {shortDate(key.createdAt)}
                  {key.lastUsedAt ? ` · last used ${shortDate(key.lastUsedAt)}` : " · never used"}
                  {key.expiresAt && ` · expires ${shortDate(key.expiresAt)}`}
                  {key.revokedAt && " · revoked"}
                </span>
              </span>
              {!key.revokedAt && (
                <button
                  type="button"
                  className="ghost danger"
                  disabled={revoke.isPending}
                  onClick={() => {
                    // A revoked key cannot be un-revoked, and whatever was
                    // using it stops working immediately.
                    if (window.confirm(`Revoke "${key.name}"? Anything using it stops working.`)) {
                      revoke.mutate(key.id);
                    }
                  }}
                >
                  Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
